import { analytics } from "@/analytics/analytics";
import { notifyAuthChanged } from "@/services/authEvents";

const API_BASE_URL: string = (import.meta.env.VITE_API_URL || "").replace(/\/$/, "");

function withBase(path: string): string {
  return `${API_BASE_URL}${path}`;
}

export type UnifiedLoginResponse = {
  role: "Admin" | "Customer" | "EndUser";
  token?: string;
  customerId?: string;
  userId?: string;
  email?: string;
  has_jgs_mapping?: boolean;
  isEval?: boolean;
};

export type IdentifyEmailResponse =
  | {
      action: "password";
      email: string;
      customerId?: string;
      customerName?: string;
      magicLinkAvailable?: boolean;
    }
  | { action: "magic_link"; email: string; customerId?: string; customerName?: string };

export async function login(email: string, password: string): Promise<UnifiedLoginResponse> {
  const resp = await fetch(withBase("/auth/login"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ email, password }),
  });
  const text = await resp.text();
  let data: any;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { raw: text };
  }
  if (!resp.ok) {
    const msg = data?.error || data?.message || "Invalid email or password";
    throw new Error(msg);
  }

  // Store token for local dev (when Secure cookies don't work from HTTP localhost)
  // Clear tokens from other roles to prevent cross-role session leakage
  const result = data as UnifiedLoginResponse;
  if (result?.token) {
    try {
      // Single token key for all roles
      sessionStorage.setItem("recruit_ai_token", result.token);
      // Clean up legacy keys
      sessionStorage.removeItem("recruit_ai_auth_token");
      sessionStorage.removeItem("recruit_ai_enduser_token");

      if (result.role === "EndUser") {
        if (result.userId) {
          sessionStorage.setItem("recruit_ai_user_id", String(result.userId));
        }
        if (typeof result.has_jgs_mapping !== "undefined") {
          sessionStorage.setItem("recruit_ai_has_jgs", String(result.has_jgs_mapping));
        }
      } else {
        // Admin/Customer: clear EndUser-specific keys
        sessionStorage.removeItem("recruit_ai_user_id");
        sessionStorage.removeItem("recruit_ai_admin_user_id");
        sessionStorage.removeItem("recruit_ai_has_jgs");
      }
      // Clean up legacy EndUser keys
      sessionStorage.removeItem("recruit_ai_enduser_user_id");
      sessionStorage.removeItem("recruit_ai_enduser_has_jgs");
    } catch (e) {
      console.error("Failed to store token:", e);
    }
  }

  // Store mapped role for analytics
  const roleMap: Record<string, string> = {
    Admin: "admin",
    Customer: "customer",
    EndUser: "end_user",
  };
  try {
    const mapped = result.isEval ? "eval" : roleMap[result.role] || "end_user";
    sessionStorage.setItem("recruit_ai_user_role", mapped);
    if (result.userId) {
      sessionStorage.setItem("recruit_ai_analytics_user_id", String(result.userId));
    }
    analytics.identify({
      userId: result.userId || null,
      email: result.email || email.trim().toLowerCase(),
      accountId: result.role === "EndUser" ? null : result.userId || null,
      customerId: result.customerId || null,
      userType: mapped as "admin" | "customer" | "end_user" | "eval",
    });
  } catch {}

  // sessionStorage changes do not emit a storage event in the same tab. Notify
  // providers that depend on the authenticated customer (for example branding).
  notifyAuthChanged();

  return result;
}

export async function identifyEmail(email: string): Promise<IdentifyEmailResponse> {
  const resp = await fetch(withBase("/auth/identify-email"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ email }),
  });
  const text = await resp.text();
  let data: any;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { raw: text };
  }
  if (!resp.ok) {
    const msg = data?.error || data?.message || "Identification failed";
    throw new Error(msg);
  }
  return data as IdentifyEmailResponse;
}
